import { CounterpartyForm } from '@/features/ap_counterparties/components/AddNewCounterpartyDialog';
import { useTranslation } from '@/i18n';
import { IgnoreErrorTracking, UnknownError } from '@/utils/errors';
import {
  useGetApCounterpartiesHook,
  useGetApCounterpartiesSuspense,
} from 'ap-openapi';
import { useCallback, useMemo } from 'react';
import { useCreateApCounterparty } from './CreateApCounterparty.service';

export const useGetApCounterparties = (query?: string) => {
  const { data, error } = useGetApCounterpartiesSuspense({ query: query });
  if (error) throw error;
  return data?.data;
};

export const useCreateApCounterpartySubmit = () => {
  const { t } = useTranslation();
  const { mutateAsync } = useCreateApCounterparty();
  return useCallback(
    async (apCounterparty: CounterpartyForm) => {
      if (apCounterparty.register_type === 'not_registered') {
        apCounterparty.invoice_registration_number = undefined;
      }
      try {
        const { status, redirectPath, errors, data } = await mutateAsync({
          data: { ap_counterparty: apCounterparty },
        });

        if (status === 'success' && data) {
          return {
            data,
            redirectPath,
          };
        } else {
          if (errors) {
            if (errors.messages) {
              throw new IgnoreErrorTracking(errors.messages.join(''));
            }
            throw new UnknownError(t('system_error'), errors);
          }
        }
      } catch (error) {
        let message;
        if (error instanceof Error) {
          throw error;
        } else {
          message = t('system_error');
        }
        throw new Error(message);
      }
    },
    [mutateAsync, t]
  );
};

export const useGetApCounterpartiesData = () => {
  const getApCounterpartiesApi = useGetApCounterpartiesHook();

  const fetchApCounterparties = useCallback(
    async (
      ids: string[]
    ): Promise<{ label: string; value: string; caption: string }[]> => {
      if (ids.length === 0) return [];
      try {
        const response = await getApCounterpartiesApi({
          ap_counterparty_ids: ids,
        });
        if (response.data.ap_counterparties) {
          return response.data.ap_counterparties.map((c) => ({
            label: c.name ?? '',
            value: c.id,
            caption: c.code ?? '',
          }));
        }
        return [];
      } catch {
        return [];
      }
    },
    [getApCounterpartiesApi]
  );

  return useMemo(() => ({ fetchApCounterparties }), [fetchApCounterparties]);
};
